"use client";

import { useState } from "react";
import Image from "next/image";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Title, Text } from "@/components/ui/Headings";

interface FounderStorySectionProps {
  title: string;
  story1: string;
  story2: string;
  story3: string;
  quote: string;
  quoteAttribution: string;
  seeMoreText?: string;
  seeLessText?: string;
}

export function FounderStorySection({
  title,
  story1,
  story2,
  story3,
  quote,
  quoteAttribution,
  seeMoreText = "See More",
  seeLessText = "See Less",
}: FounderStorySectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section className="pt-4 pb-4">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-start">
          {/* Mobile: Photo First, Desktop: Text First */}
          <div className="order-1 lg:order-2 relative">
            <Image
              src="/images/lesia-founder.jpg"
              alt="Lesia Povkh, Founder of Revoyager"
              width={600}
              height={400}
              className="rounded-2xl shadow-2xl object-cover w-full"
            />
            <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 bg-white p-4 sm:p-6 rounded-xl shadow-xl max-w-[90%] sm:max-w-xs">
              <Text variant="caption" className="text-gray-900 italic mb-2">
                &ldquo;{quote}&rdquo;
              </Text>
              <Text variant="caption" className="text-gray-500 text-[10px] sm:text-xs">
                {quoteAttribution}
              </Text>
            </div>
          </div>

          {/* Mobile: Text Second (with See More), Desktop: Text First (full) */}
          <div className="order-2 lg:order-1">
            <Title size="md" className="mb-2">
              {title}
            </Title>
            
            {/* Mobile: Truncated/Expandable Text */}
            <div className="lg:hidden">
              <Text variant="body" className="text-gray-700 mb-2 indent-4 leading-relaxed text-justify">
                {story1}
              </Text>
              
              {/* Collapsible content on mobile */}
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  isExpanded ? "max-h-[1000px]" : "max-h-0"
                }`}
              >
                <Text variant="body" className="text-gray-700 mb-2 leading-relaxed text-justify">
                  {story2}
                </Text>
                <Text variant="body" className="text-gray-700 mb-2 leading-relaxed text-justify">
                  {story3}
                </Text>
              </div>

              {/* See More/Less Button - Mobile Only */}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium text-sm mt-3 transition-colors"
                aria-label={isExpanded ? seeLessText : seeMoreText}
              >
                <span>{isExpanded ? seeLessText : seeMoreText}</span>
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            </div>

            {/* Desktop: Full Text Always Visible */}
            <div className="hidden lg:block">
              <Text variant="body" className="text-gray-700 mb-2 indent-4 leading-relaxed text-justify">
                {story1}
              </Text>
              <Text variant="body" className="text-gray-700 mb-2 leading-relaxed text-justify">
                {story2}
              </Text>
              <Text variant="body" className="text-gray-700 mb-2 leading-relaxed text-justify">
                {story3}
              </Text>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

